<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { useRouter } from 'vue-router'
import { computed, ref } from 'vue'
import { formatCurrency } from '@/util/formatters'
import StatusBadge from '@/components/common/StatusBadge.vue'
import DetailsViewLayout from '@/views/layouts/DetailsViewLayout.vue'
import { useClaims } from '@/composables/asicw/claims'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import ClaimEditDialog from '@/components/asicw/claims/ClaimEditDialog.vue'
import { getFailedPartIcon } from '@/components/asicw/common/utils'
import { ClaimStatus, ClaimStatusTypes, type ClaimFailurePart, type IClaimDashboard } from '@/types/asicw/claims'
import { useOIDCStore } from '@/stores/oidc.store'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import ClaimStatusTag from '../common/ClaimStatusTag.vue'
import { currencyFormatter } from '@/helpers/ep/formatters'
import ClaimCostsDialog from './ClaimCostsDialog.vue'
import { DateTime } from 'luxon'
import { downloadLinkTarget } from '@/util'
import ClaimHistoryDialog from './ClaimHistoryDialog.vue'

const props = defineProps<{ claimNumber: string }>()
const router = useRouter()

const { isASICWOperationalUser, isASICWManagerUser, isASICWViewerUser } = useOIDCStore()

const { getClaimDetailsQuery, deleteClaimMutation } = useClaims()
const claimNumber = computed(() => props.claimNumber)

const { data, isError, isRefetching, error, refetch } = getClaimDetailsQuery(claimNumber)

const showEditDialog = ref(false)
const showCostsDialog = ref(false)
const showClaimHistoryDialog = ref(false)
const claimBeingUpdated = ref<IClaimDashboard>()

const handleClaimEdit = () => {
  showEditDialog.value = true
}

const handleClaimCostsEdit = () => {
  if (!data.value) return
  const claimDashboard: IClaimDashboard = {
    ...data.value,
    failures: data.value.failures ? data.value.failures.length : 0,
  }
  claimBeingUpdated.value = claimDashboard
  showCostsDialog.value = true
}

const handleClaimDelete = () => {
  DeleteConfirmationModal(
    'Delete Claim',
    'Are you sure you want to delete this claim? This action cannot be undone.',
    props.claimNumber,
    deleteClaimMutation.mutateAsync,
    () => {
      router.push({ name: 'asicw-claims' })
    }
  )
}

const handleDeleteDisabled = (status: ClaimStatus) => {
  const deletableStatuses = [ClaimStatus.UnderReview, ClaimStatus.Denied]
  return !deletableStatuses.includes(status)
}
</script>

<template>
  <DetailsViewLayout back-button-text="Back" :title="`Claim #${claimNumber}`" @on-back-click="router.back()">
    <template v-if="isASICWOperationalUser" #controls>
      <div class="flex gap-2">
        <el-button v-if="isASICWManagerUser" type="primary" @click="handleClaimCostsEdit">
          <Icon icon="material-symbols:attach-money" :height="16" />
          <span class="m-2">Costs</span>
        </el-button>
        <el-button type="primary" @click="showClaimHistoryDialog = true">
          <Icon icon="material-symbols:history" :height="16" />
          <span class="m-2">History</span>
        </el-button>
        <el-button type="primary" :disabled="data?.status === ClaimStatus.Invoiced" @click="handleClaimEdit">
          <Icon icon="material-symbols:edit-outline" :height="16" />
          <span class="m-2">Edit</span>
        </el-button>
        <el-button
          :disabled="handleDeleteDisabled(data?.status as ClaimStatus)"
          type="primary"
          @click="handleClaimDelete"
        >
          <Icon icon="material-symbols:delete-outline" :height="16" />
          <span class="m-2">Delete</span>
        </el-button>
      </div>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else-if="data">
      <div class="main-content flex flex-wrap gap-6">
        <div class="flex-1 min-w-[49%] flex flex-col gap-6">
          <!-- Overview Card -->
          <el-card class="h-fit">
            <template #header>
              <div class="font-semibold text-text-tertiary">OVERVIEW</div>
            </template>
            <div class="flex flex-col gap-4">
              <div v-if="data.created_at" class="flex flex-col">
                <div class="text-text-tertiary">Created at</div>
                <div>{{ DateTime.fromISO(data.created_at).toFormat('MM/dd/yyyy') }}</div>
              </div>
              <div v-if="data.date_of_loss" class="flex flex-col">
                <div class="text-text-tertiary">Date of Loss</div>
                <div>{{ DateTime.fromISO(data.date_of_loss).toFormat('MM/dd/yyyy') }}</div>
              </div>
              <div v-if="data.miner?.model_family" class="flex flex-col">
                <div class="text-text-tertiary">Miner Model Family</div>
                <div>{{ data.miner.model_family }}</div>
              </div>
              <div v-if="data.policy_number" class="flex flex-col mr-auto">
                <div class="text-text-tertiary">Policy Number</div>
                <el-tooltip
                  effect="light"
                  placement="right"
                  :show-after="100"
                  :hide-after="100"
                  :show-arrow="false"
                  class="mr-auto"
                >
                  <template #content>
                    <div class="flex flex-col gap-4 p-2">
                      <div class="flex flex-col">
                        <div class="text-text-tertiary">Group</div>
                        <div>{{ data.policy.group || '-' }}</div>
                      </div>
                      <div class="flex flex-col">
                        <div class="text-text-tertiary">Miner SN</div>
                        <div>{{ data.policy?.miner_sn || '-' }}</div>
                      </div>
                      <div class="flex flex-col">
                        <div class="text-text-tertiary">SKU</div>
                        <div>{{ data.policy?.sku || '-' }}</div>
                      </div>
                      <div class="flex flex-col">
                        <div class="text-text-tertiary">Location</div>
                        <div>{{ data.policy?.location || '-' }}</div>
                      </div>
                      <div class="flex flex-col">
                        <div class="text-text-tertiary">Status</div>
                        <div v-if="data.policy?.status"><StatusBadge :status="data.policy.status" /></div>
                        <div v-else>-</div>
                      </div>
                    </div>
                  </template>
                  <div class="border-b border-dashed border-text-secondary cursor-help">{{ data.policy_number }}</div>
                </el-tooltip>
              </div>
              <div class="flex flex-col gap-2">
                <div class="text-text-tertiary">Claim and Repair Status</div>
                <div class="flex gap-2">
                  <ClaimStatusTag :status="data.status" :type="ClaimStatusTypes.ClaimStatus" :readonly="true" />
                  <ClaimStatusTag
                    v-if="data.repair_status"
                    :status="data.repair_status"
                    :type="ClaimStatusTypes.RepairStatus"
                    :readonly="true"
                  />
                </div>
              </div>
              <div v-if="data.reject_reason" class="flex flex-col gap-2">
                <div class="text-text-tertiary">Reject Reason</div>
                <div>{{ data.reject_reason }}</div>
              </div>
              <div class="flex flex-col">
                <div class="text-text-tertiary">RWO Number</div>
                <div>
                  <template v-if="data.work_order_number">
                    <ElLink
                      @click="
                        () =>
                          router.push({
                            name: 'rc-work-order-dashboard',
                            params: { number: data!.work_order_number },
                          })
                      "
                    >
                      {{ data.work_order_number }}
                    </ElLink>
                  </template>
                  <template v-else>-</template>
                </div>
              </div>
              <div v-if="isASICWManagerUser || isASICWViewerUser" class="flex flex-col">
                <div class="text-text-tertiary">Total Cost</div>
                <div>{{ data.costs?.total ? formatCurrency(data.costs.total) : '-' }}</div>
              </div>
            </div>
          </el-card>

          <!-- Failures Card -->
          <el-card v-if="data.failures?.length" class="h-fit">
            <template #header>
              <div class="font-semibold text-text-tertiary">FAILURES</div>
            </template>
            <div class="flex flex-col gap-4">
              <div v-for="(failure, index) in data.failures" :key="index" class="flex flex-col gap-3">
                <div class="flex items-center gap-2">
                  <Icon :icon="getFailedPartIcon(failure.part as ClaimFailurePart)" height="20" />
                  <div class="text-text-tertiary font-medium">{{ failure.part }}</div>
                </div>

                <!-- Part Details -->
                <div
                  v-if="failure.part_sn || failure.part_model || failure.part_number"
                  class="pl-7 flex flex-col gap-2"
                >
                  <div v-if="failure.part_sn" class="flex flex-col">
                    <div class="text-text-tertiary text-sm">Serial Number</div>
                    <div class="text-sm">{{ failure.part_sn }}</div>
                  </div>
                  <div v-if="failure.part_model" class="flex flex-col">
                    <div class="text-text-tertiary text-sm">Model</div>
                    <div class="text-sm">{{ failure.part_model }}</div>
                  </div>
                  <div v-if="failure.part_number" class="flex flex-col">
                    <div class="text-text-tertiary text-sm">Part Number</div>
                    <div class="text-sm">{{ failure.part_number }}</div>
                  </div>
                </div>

                <!-- Failure Reason -->
                <div class="pl-7 flex flex-col">
                  <div class="text-text-tertiary text-sm">Reason</div>
                  <div class="text-sm">{{ failure.reason }}</div>
                </div>
              </div>
            </div>
          </el-card>
        </div>

        <div class="flex-1 min-w-[49%] flex flex-col gap-6">
          <!-- Costs Card -->
          <el-card
            v-if="data.costs?.details && data.costs.details.length > 0 && (isASICWManagerUser || isASICWViewerUser)"
            class="h-fit"
          >
            <template #header>
              <div class="font-semibold text-text-tertiary">COST DETAILS</div>
            </template>
            <el-table :data="data.costs.details" style="width: 100%">
              <el-table-column prop="charge" label="Charge" />
              <el-table-column prop="cost" label="Cost" :formatter="currencyFormatter" />
              <el-table-column prop="payable_from" label="Payable From" />
              <el-table-column prop="payable_to" label="Payable To" />
            </el-table>
          </el-card>

          <!-- Container Card -->
          <el-card class="h-fit">
            <template #header>
              <div class="font-semibold text-text-tertiary">PACKAGE DIMENSIONS</div>
            </template>
            <div v-if="data.container_data" class="flex flex-row gap-10">
              <div class="flex flex-col gap-2">
                <span class="text-text-tertiary">Type</span>
                <span>{{ data.container_data.type }}</span>
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-text-tertiary">Length (in)</span>
                <span>{{ data.container_data.length }}</span>
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-text-tertiary">Width (in)</span>
                <span>{{ data.container_data.width }}</span>
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-text-tertiary">Height (in)</span>
                <span>{{ data.container_data.height }}</span>
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-text-tertiary">Weight (lb)</span>
                <span>{{ data.container_data.weight }}</span>
              </div>
            </div>
            <div v-else class="flex flex-col gap-2">
              <span>Not specified</span>
            </div>
          </el-card>

          <!-- Details Card -->
          <el-card class="h-fit">
            <template #header>
              <div class="font-semibold text-text-tertiary">DETAILS</div>
            </template>
            <div class="flex flex-col gap-6">
              <div v-if="!data.notes && !data.pictures?.length">
                <RlEmptyDataset title="This claim has no notes nor pictures" />
              </div>
              <div v-if="data.notes">
                <div class="whitespace-pre-wrap">{{ data.notes }}</div>
              </div>
              <div v-if="data.pictures?.length">
                <div class="grid grid-cols-3 gap-4">
                  <div v-for="picture in data.pictures" :key="picture.id" class="aspect-square">
                    <el-image
                      :src="picture.picture_url"
                      :alt="picture.name"
                      fit="cover"
                      class="w-full h-full object-cover rounded"
                      :preview-src-list="[picture.picture_url]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </el-card>

          <!-- Log Files Card -->
          <el-card class="h-fit">
            <template #header>
              <div class="font-semibold text-text-tertiary">LOG FILES</div>
            </template>
            <div class="flex flex-col gap-4">
              <div v-if="data.log_files?.length" class="flex flex-col gap-3">
                <div
                  v-for="logFile in data.log_files"
                  :key="logFile.id"
                  class="flex items-center justify-between p-3 bg-fill-tertiary rounded-lg"
                >
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-medium truncate">{{ logFile.name }}</div>
                  </div>
                  <el-button
                    type="primary"
                    size="small"
                    class="flex-shrink-0"
                    @click="downloadLinkTarget(logFile.file_url, logFile.name)"
                  >
                    <Icon icon="material-symbols:download" :height="16" />
                    <span class="ml-1">Download</span>
                  </el-button>
                </div>
              </div>
              <div v-else>
                <RlEmptyDataset title="This claim has no log files" />
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </template>
    <claim-edit-dialog v-if="showEditDialog" v-model="showEditDialog" :claim-number="claimNumber" />
    <ClaimCostsDialog v-model="showCostsDialog" :claim="claimBeingUpdated" />
    <ClaimHistoryDialog v-if="showClaimHistoryDialog" v-model="showClaimHistoryDialog" :claim-number="claimNumber" />
  </DetailsViewLayout>
</template>

<style scoped>
.el-card {
  background-color: var(--fill-secondary);

  --el-card-border-color: transparent;
  --el-card-border-radius: 8px;

  box-shadow: none;
}

:deep(.el-card__header) {
  border-bottom: none;
  padding-bottom: unset;
}
</style>
